import type { Message } from '@nexo/types';
import {
  Avatar,
  Button,
  IconButton,
  Textarea,
  cn,
  useContextMenu,
  useToast,
  type MenuEntries,
} from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bookmark,
  BookmarkCheck,
  Bot,
  Copy,
  Link2,
  ListPlus,
  MessageSquareText,
  Pencil,
  Pin,
  PinOff,
  Reply,
  Trash2,
} from 'lucide-react';
import { memo, useState } from 'react';
import { mentionedRoleIds, mentionedUserIds } from '@nexo/types';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { formatTime } from '../../lib/format';
import { useSavedIds } from '../../lib/queries';
import { useMe } from '../auth/AuthProvider';
import { StatusButton } from '../tasks/TaskIcons';
import { useUpdateTask } from '../tasks/useTaskMutations';
import { CallRecord } from '../calls/CallRecord';
import { MeetingCard } from '../meetings/MeetingView';
import { MessageAttachments } from './MessageAttachments';
import { MessageEmbeds } from './MessageEmbeds';
import { EmojiPicker } from './EmojiPicker';
import {
  LinkPreviews,
  MessageReactions,
  PinnedLabel,
  QUICK_REACTIONS,
  ThreadLink,
  useMessageActions,
} from './MessageExtras';
import { PollCard } from './PollCard';
import { openUserCard } from '../teams/UserCard';
import { useRemindMe } from './useRemindMe';
import { QuoteBlock } from './QuoteBlock';
import { RichText } from './RichText';
import { replyStore } from './replying';
import { UserBadges } from '../status/UserBadges';
import { editingStore, useEditingMessageId } from './editing';
import { fromEditable, toEditable } from './mentions';
import { canIn } from '../../lib/permissions';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../../lib/forms';

export const isPending = (message: Message) => message.id.startsWith('temp-');

export const MessageItem = memo(function MessageItem({
  message,
  compact,
  inThread = false,
}: {
  message: Message;
  compact: boolean;
  /** Rendered inside a thread panel: no "reply in thread" action nor thread summary. */
  inThread?: boolean;
}) {
  const me = useMe();
  const { team, channels, openTask, roleById, memberById, openThread } = useTeam();
  const savedIds = useSavedIds(team.id);
  const actions = useMessageActions(message);
  const saved = savedIds.data?.has(message.id) ?? false;
  const queryClient = useQueryClient();
  const toast = useToast();
  const updateTask = useUpdateTask();
  const { t } = useTranslation();
  const editing = useEditingMessageId() === message.id;

  const isBot = message.webhookId !== null;
  const isMine = !isBot && message.author.id === me.id;
  const channel = channels.find((c) => c.id === message.channelId);
  const canDelete = isMine || canIn(channel, 'manage_messages');
  const canReact = canIn(channel, 'add_reactions');
  const canReply = canIn(channel, 'send_messages');
  const canPin = canIn(channel, 'pin_messages');
  const mentionsMe =
    !isMine &&
    (mentionedUserIds(message.body).includes(me.id) ||
      /(^|\s)@canal\b/.test(message.body) ||
      mentionedRoleIds(message.body).some(
        (id) => roleById(id)?.mentionable && memberById(me.id)?.customRoleIds.includes(id),
      ));
  const pending = isPending(message);
  // Only messages that arrive while the channel is open animate in, not the history.
  const [fresh] = useState(() => Date.now() - new Date(message.createdAt).getTime() < 5000);

  const remove = useMutation({
    mutationFn: () => api.messages.remove(message.id),
    onSuccess: () =>
      cache.removeMessage(queryClient, message.channelId, (m) => m.id === message.id),
    onError: (err) =>
      toast({ title: t('No se pudo borrar'), description: errorMessage(err), tone: 'error' }),
  });

  const createTask = () =>
    openTask({
      mode: 'create',
      title: message.body.split('\n')[0]?.slice(0, 200),
      channelId: message.channelId,
      sourceMessageId: message.id,
    });

  const menu = useContextMenu();
  const remind = useRemindMe(message);
  const copy = (text: string, title: string) => {
    void navigator.clipboard.writeText(text);
    toast({ title, tone: 'success' });
  };
  const askRemove = () => {
    if (window.confirm(t('¿Borrar este mensaje?'))) remove.mutate();
  };
  const canQuote = canReply && !inThread && !message.call && !message.meetingId;
  const canThread = canReply && !inThread && !message.call && !message.parentId;
  const canPinIt = canPin && !message.call && !message.parentId;
  const canEdit = isMine && !message.call && !message.meetingId && !message.poll;

  // Right click: the same actions as the hover toolbar, plus copying (like Discord).
  const menuEntries = (): MenuEntries => [
    canReact && {
      quick: QUICK_REACTIONS.slice(0, 5).map((emoji) => ({
        label: t('Reaccionar con {{emoji}}', { emoji }),
        content: emoji,
        onSelect: () => actions.toggleReaction(emoji),
      })),
    },
    canReact && ('separator' as const),
    canQuote && { label: t('Responder'), icon: <Reply />, onSelect: () => replyStore.set(message) },
    canThread && {
      label: t('Responder en hilo'),
      icon: <MessageSquareText />,
      onSelect: () => openThread(message.id),
    },
    !message.call && { label: t('Convertir en tarea'), icon: <ListPlus />, onSelect: createTask },
    canPinIt && {
      label: message.pinnedAt ? t('Desfijar') : t('Fijar en la conversación'),
      icon: message.pinnedAt ? <PinOff /> : <Pin />,
      onSelect: () => actions.pin.mutate(message.pinnedAt === null),
    },
    {
      label: saved ? t('Quitar de guardados') : t('Guardar para más tarde'),
      icon: saved ? <BookmarkCheck /> : <Bookmark />,
      onSelect: () => actions.save.mutate(!saved),
    },
    'separator' as const,
    ...(pending ? [] : remind.entries),
    'separator' as const,
    message.body !== '' && {
      label: t('Copiar texto'),
      icon: <Copy />,
      onSelect: () => copy(message.body, t('Texto copiado')),
    },
    {
      label: t('Copiar enlace al mensaje'),
      icon: <Link2 />,
      onSelect: () =>
        copy(
          `${window.location.origin}/app/t/${team.id}/c/${message.channelId}?m=${message.id}`,
          t('Enlace copiado'),
        ),
    },
    (canEdit || canDelete) && ('separator' as const),
    canEdit && {
      label: t('Editar'),
      icon: <Pencil />,
      onSelect: () => editingStore.set(message.id),
    },
    canDelete && { label: t('Borrar'), icon: <Trash2 />, danger: true, onSelect: askRemove },
  ];

  return (
    <article
      id={`message-${message.id}`}
      onContextMenu={editing || pending ? undefined : menu.open(menuEntries)}
      className={cn(
        'group relative flex gap-3 px-6 transition-colors hover:bg-surface-2/70',
        mentionsMe &&
          'bg-highlight/10 shadow-[inset_3px_0_0_var(--color-highlight)] hover:bg-highlight/15',
        compact ? 'py-0.5' : 'mt-3 pt-1 pb-0.5',
        pending && 'opacity-55',
        fresh && 'animate-rise',
      )}
    >
      <div className="w-9 shrink-0">
        {compact ? (
          <time
            className="invisible block pt-[3px] text-right text-[10px] text-subtle tabular-nums group-hover:visible"
            dateTime={message.createdAt}
          >
            {formatTime(message.createdAt)}
          </time>
        ) : (
          <MessageAvatar message={message} />
        )}
      </div>

      <div className="min-w-0 flex-1">
        {!compact && (
          <header className="flex items-baseline gap-2">
            {isBot ? (
              <span className="text-sm font-semibold">{message.author.displayName}</span>
            ) : (
              <button
                type="button"
                data-user-card={message.author.id}
                onClick={(event) => openUserCard(message.author.id, event)}
                className="text-sm font-semibold hover:underline"
              >
                {message.author.displayName}
              </button>
            )}
            {!isBot && memberById(message.author.id) && (
              <UserBadges user={memberById(message.author.id) ?? message.author} />
            )}
            {isBot && (
              <span className="self-center rounded bg-accent px-1 py-px text-[9.5px] font-bold tracking-wide text-accent-fg">
                APP
              </span>
            )}
            <time className="text-xs text-subtle tabular-nums" dateTime={message.createdAt}>
              {formatTime(message.createdAt)}
            </time>
          </header>
        )}

        <PinnedLabel message={message} />
        {message.replyTo && <QuoteBlock quote={message.replyTo} className="mt-0.5 mb-1" />}
        {message.meetingId ? (
          <MeetingCard meetingId={message.meetingId} />
        ) : message.poll ? (
          <PollCard message={message} poll={message.poll} />
        ) : message.call ? (
          <CallRecord message={message} call={message.call} />
        ) : editing ? (
          <EditMessageForm message={message} />
        ) : (
          (message.body || message.editedAt) && (
            <p className="text-[14.5px] leading-relaxed break-words whitespace-pre-wrap">
              <RichText text={message.body} />
              {message.editedAt && (
                <span className="ml-1.5 text-[11px] text-subtle">{t('(editado)')}</span>
              )}
            </p>
          )
        )}

        <MessageAttachments attachments={message.attachments} />
        <MessageEmbeds embeds={message.embeds} />
        <LinkPreviews previews={message.previews} />
        <MessageReactions message={message} />
        {!inThread && <ThreadLink message={message} />}

        {message.tasks.length > 0 && (
          <div className="mt-2 mb-1 flex flex-col gap-1.5">
            {message.tasks.map((linked) => (
              <div
                key={linked.id}
                className="flex max-w-md items-center gap-2.5 rounded-xl border border-border bg-surface px-3 py-2 shadow-panel transition-colors hover:border-border-strong"
              >
                <StatusButton
                  status={linked.status}
                  onChange={(status) => updateTask.mutate({ id: linked.id, input: { status } })}
                />
                <button
                  type="button"
                  onClick={() => openTask({ mode: 'edit', taskId: linked.id })}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="block text-[11px] font-medium tracking-wide text-subtle uppercase">
                    {t('Tarea')}
                  </span>
                  <span
                    className={cn(
                      'block truncate text-sm font-medium',
                      linked.status === 'done' && 'text-subtle line-through',
                    )}
                  >
                    {linked.title}
                  </span>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {!editing && !pending && (
        <div className="absolute -top-3.5 right-5 hidden items-center gap-0.5 rounded-lg border border-border bg-surface p-0.5 shadow-float group-focus-within:flex group-hover:flex">
          {canReact &&
            QUICK_REACTIONS.slice(0, 3).map((emoji) => (
              <button
                key={emoji}
                type="button"
                title={t('Reaccionar con {{emoji}}', { emoji })}
                onClick={() => actions.toggleReaction(emoji)}
                className="inline-flex size-7 items-center justify-center rounded-md text-base hover:bg-surface-2"
              >
                {emoji}
              </button>
            ))}
          {canReact && (
            <>
              <EmojiPicker
                size="sm"
                label={t('Reaccionar')}
                align="right"
                placement="bottom"
                onPick={(emoji) => actions.toggleReaction(emoji)}
              />
              <span className="mx-0.5 h-5 w-px bg-border" />
            </>
          )}
          {canQuote && (
            <IconButton label={t('Responder')} size="sm" onClick={() => replyStore.set(message)}>
              <Reply />
            </IconButton>
          )}
          {canThread && (
            <IconButton
              label={t('Responder en hilo')}
              size="sm"
              onClick={() => openThread(message.id)}
            >
              <MessageSquareText />
            </IconButton>
          )}
          {!message.call && (
            <IconButton label={t('Convertir en tarea')} size="sm" onClick={createTask}>
              <ListPlus />
            </IconButton>
          )}
          {canPinIt && (
            <IconButton
              label={message.pinnedAt ? t('Desfijar') : t('Fijar en la conversación')}
              size="sm"
              active={message.pinnedAt !== null}
              onClick={() => actions.pin.mutate(message.pinnedAt === null)}
            >
              {message.pinnedAt ? <PinOff /> : <Pin />}
            </IconButton>
          )}
          <IconButton
            label={saved ? t('Quitar de guardados') : t('Guardar para más tarde')}
            size="sm"
            active={saved}
            onClick={() => actions.save.mutate(!saved)}
          >
            {saved ? <BookmarkCheck /> : <Bookmark />}
          </IconButton>
          {canEdit && (
            <IconButton
              label={t('Editar')}
              size="sm"
              onClick={() => {
                editingStore.set(message.id);
              }}
            >
              <Pencil />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              label={t('Borrar')}
              size="sm"
              className="hover:text-danger"
              onClick={askRemove}
            >
              <Trash2 />
            </IconButton>
          )}
        </div>
      )}
      {menu.element}
      {remind.element}
    </article>
  );
});

function MessageAvatar({ message }: { message: Message }) {
  if (message.webhookId && !message.author.avatarUrl) {
    return (
      <span className="mt-0.5 flex size-9 items-center justify-center rounded-full bg-accent-soft text-accent">
        <Bot className="size-5" />
      </span>
    );
  }
  const avatar = (
    <Avatar
      id={message.author.id}
      name={message.author.displayName}
      src={message.author.avatarUrl}
      className="mt-0.5"
    />
  );
  if (message.webhookId) return avatar;
  return (
    <button
      type="button"
      data-user-card={message.author.id}
      onClick={(event) => openUserCard(message.author.id, event)}
      aria-label={message.author.displayName}
      className="rounded-full transition-opacity hover:opacity-85"
    >
      {avatar}
    </button>
  );
}

function EditMessageForm({ message }: { message: Message }) {
  const { members, roles } = useTeam();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => toEditable(message.body, members, roles));
  const close = () => editingStore.set(null);

  const edit = useMutation({
    mutationFn: () =>
      api.messages.edit(message.id, { body: fromEditable(draft.trim(), members, roles) }),
    onSuccess: (updated) => {
      cache.upsertMessage(queryClient, updated);
      close();
    },
    onError: (err) =>
      toast({ title: t('No se pudo editar'), description: errorMessage(err), tone: 'error' }),
  });

  return (
    <form
      className="mt-1 flex flex-col gap-2 pb-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (draft.trim()) edit.mutate();
      }}
    >
      <Textarea
        autoFocus
        value={draft}
        onFocus={(e) => e.currentTarget.setSelectionRange(draft.length, draft.length)}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') close();
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (draft.trim()) edit.mutate();
          }
        }}
      />
      <div className="flex items-center gap-2 text-xs text-subtle">
        <Button size="sm" type="submit" loading={edit.isPending}>
          {t('Guardar')}
        </Button>
        <Button size="sm" variant="ghost" onClick={close}>
          {t('Cancelar')}
        </Button>
        <span className="max-sm:hidden">{t('Esc para cancelar · Enter para guardar')}</span>
      </div>
    </form>
  );
}
